import { Component, Input, OnInit, ViewChild, AfterViewInit, EventEmitter, Output, SimpleChanges, OnChanges, inject } from '@angular/core';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CommonModule, DatePipe, NgFor, NgIf } from '@angular/common';
import { ColumnConfig } from '../models/material-common-models';
import { MaterialModule } from '../material.module';
import { GenericExtensionsService } from '../../extensions/generic-extensions-service';
import { MasterDataService } from '../../dynamic-controls/master-data-service';
import { IUserInfoResponse } from '../../features/requester-access/models/get-user-info-response.model';
import { catchError, finalize, map } from 'rxjs';
import { SpinnerService } from '../../core';
import { ResizeColumnDirective } from '../../directives/resize-column.directive';
import { EllipsisLeftDirective } from '../../directives/ellipsis-left.directive';
import { TooltipDisableDirective } from '../custom-directives/tooltip.directive';
import { NoDocumentFoundComponent } from '../no-document-found/no-document-found.component';

@Component({
    selector: 'app-dynamic-table',
    imports: [MaterialModule, MatSortModule, MatTableModule, CommonModule, NgIf, NgFor, DatePipe, TooltipDisableDirective, ResizeColumnDirective, EllipsisLeftDirective, NoDocumentFoundComponent],
    templateUrl: './dynamic-table.component.html',
    styleUrls: ['./dynamic-table.component.scss', './dynamic-table-componentSpecific.style.scss'],
    providers: [GenericExtensionsService]
})
export class DynamicTableComponent implements OnInit, AfterViewInit,OnChanges {
  @ViewChild(MatSort) sort: MatSort | undefined;
  dataSource: MatTableDataSource<any> = new MatTableDataSource([]);

  @Input() columnList: ColumnConfig[] = [];

  /*
   * Example column configuration:

   * - CheckBox: A clickable checkbox column, not sortable.
   * - Name: A clickable general column with ellipsis at the back, sortable.
   * - Age: A non-clickable general column, sortable.
   * - Location: A non-clickable general column with ellipsis at the front, not sortable.
   * - UploadedOn: A non-clickable date column, sortable.
   * - ProvideJustification: A clickable column with no header, not sortable.
   * 
   * 
    this.columnList = [
    { columnName: 'CheckBox', isClickable: true , ellipsisDirection:EllipsisDirection.None, columnType:ColumnType.CheckBox, isSortable:false},
    { columnName: 'Name', isClickable: true , ellipsisDirection:EllipsisDirection.Back, columnType:ColumnType.General, isSortable:true},
    { columnName: 'Age', isClickable: false, ellipsisDirection:EllipsisDirection.None, columnType:ColumnType.General, isSortable:true },
    { columnName: 'Location', isClickable:false, ellipsisDirection:EllipsisDirection.Front, columnType:ColumnType.General, isSortable:false },
    { columnName: 'UploadedOn', isClickable:false, ellipsisDirection:EllipsisDirection.None, columnType:ColumnType.DateType, isSortable:true  },
    { columnName: 'ProvideJustification', isClickable:true, ellipsisDirection:EllipsisDirection.None, columnType:ColumnType.NullHeader, isSortable:false  }
  ];

   */

  @Input() tableItems: any[] = [];
  /*
   * Example tableItems Values
   * - Property Name should match Column value
   * 
    tableItems = [
    { Name: 'John Walter', Age: 30, Location: 'New York', UploadedOn:new Date().toString(), ProvideJustification: 'Provide Justification' },
    { Name: 'Jane Smith', Age: 25, Location: 'London', UploadedOn:new Date().toString(), ProvideJustification: 'Provide Justification' },
    { Name: 'Michael Johnson', Age: 35, Location: 'Sydney', UploadedOn:new Date().toString(), ProvideJustification: 'Provide Justification' }
  ];
  */

  @Input() tableWidthInVw: number = 93.5;
  @Input() tableHeightInVh: number = 76;
  @Input() borderSizeInPx: number = 20;
  @Input() enableGlobalCheckBox: boolean = false;
  @Input() noItemsErrorMessage: string = "No items to be displayed";
  @Input() hoverDisplay: boolean = true;
  @Input() hideHeader: boolean = false;
  // Advanced selection/highlight rules toggle
  @Input() advancedSelectionBehavior: boolean = false;
  @Input() idPrefix: string | null = null;

  @Output() public checkedItems = new EventEmitter<any[]>();
  @Output() public clickedRowData = new EventEmitter<{ event:Event, columnName: string, rowData: any}>();


  columnNames: string[] = [];
  allItemsChecked: boolean = false;
  clickedRow : number = 0;
  hoverIndex : number = -1;
  private lastToggledRowId: number | null = null;
  userInformationFetched = false;
  userInfos:IUserInfoResponse[] = [];
  puidColumnName = "";

  genericService = inject(GenericExtensionsService);
  private masterDataService = inject(MasterDataService);
  private spinnerService = inject(SpinnerService);
  private readonly defaultFeatureToken = 'shared';
  private readonly defaultSurfaceToken = 'dynamic-table';
  
  ngOnInit(): void {
    this.updateTableChanges();
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  public ngOnChanges(changes:SimpleChanges): void {
    if(changes?.columnList?.currentValue || changes?.tableItems?.currentValue){
      this.updateTableChanges();
    }
  }

  private updateTableChanges(){
    this.columnNames = this.columnList.map(x => x.columnName);
    let rowId = 0;
    if (this.columnList.some(column => column.columnType === 'checkbox')) {
      this.tableItems = this.tableItems.map(item => ({ ...item, isChecked: false, rowId:rowId++ }));
    } else {
      this.tableItems = this.tableItems.map(item => ({ ...item, rowId:rowId++ }));
    }
    if(this.columnList.some(column => column.isPuid === true && this.tableItems.length>0)){
      if(!this.userInformationFetched){
        this.puidColumnName = this.columnList.find(c => c.isPuid === true).columnName;
        this.getUserInfo();
      }
      else{
        this.tableItems = this.tableItems.map(item => ({ ...item, userName:"", email:""}));
          for(let i=0;i<this.tableItems.length;i++){
            let userInfo = this.userInfos.find(x => x.PUID === this.tableItems[i][this.puidColumnName]);
            this.tableItems[i].userName = userInfo?.Name;
            this.tableItems[i].email = userInfo?.Email;
          }
          this.updateDataSource();
      }
    }
    else{
      this.updateDataSource();
    }
  }

  updateDataSource(){
    this.dataSource = new MatTableDataSource(this.tableItems);
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    if (this.columnList.some(column => column.columnType === 'checkbox')) {
      this.updateCheckBoxes();
    }
  }

  clickedData(clickedData:{event:Event, columnConfig: ColumnConfig, rowData: any}) {
    if(clickedData.columnConfig?.columnType==='nullheader'){
      this.dataSource.data.forEach((item) => (item.isChecked = false));
      this.dataSource.data.find(item=>item.rowId===clickedData.rowData.rowId)!.isChecked = true;
      this.updateCheckBoxes();
    }
    this.clickedRowData.emit({event:clickedData.event, columnName: clickedData.columnConfig?.columnName, rowData: clickedData.rowData});
  }

  public someChecked(): boolean {
    if (this.dataSource.data == null) {
      return false;
    }
    return (
      this.dataSource.data.filter((t) => t.isChecked).length > 0 &&
      !this.allItemsChecked
    );
  }

  public setAll(checked: boolean) {
    this.allItemsChecked = checked;
    if (this.dataSource.data && this.dataSource.data.length > 0) {
      this.dataSource.data.forEach((item) => (item.isChecked = checked));
    }
    this.updateCheckBoxes();
  }

  public updateCheckBoxes() {
    this.allItemsChecked =
    this.dataSource.data && this.dataSource.data.every((item) => item.isChecked);
    this.checkedItems.emit(this.dataSource.data.filter(item => item.isChecked));
    if (this.advancedSelectionBehavior) {
      this.applyAdvancedSelectionRules();
    }
  }

  public toggleRowSelection(row: any, event: Event): void {
    event.stopPropagation();
    row.isChecked = !row.isChecked;
    this.lastToggledRowId = row.rowId;
    this.updateCheckBoxes();
  }

  private applyAdvancedSelectionRules(): void {
    const selected = this.dataSource.data.filter(x => x.isChecked);
    const count = selected.length;
    if (count === 0) {
      if (this.dataSource.data.length > 0) {
        this.clickedRow = this.dataSource.data[0].rowId;
      }
    } else if (count === 1) {
      this.clickedRow = selected[0].rowId;
    } else {
      let target = selected.find(x => x.rowId === this.lastToggledRowId);
      if (!target) target = selected[selected.length - 1];
      this.clickedRow = target.rowId;
    }
  }

  public onRowClick(row: any): void {
    if (!this.advancedSelectionBehavior) {
      this.clickedRow = row.rowId;
      return;
    }
    const selected = this.dataSource.data.filter(x => x.isChecked);
    const count = selected.length;
    if ((count === 1 || count > 1) && !row.isChecked) {
      return; // enforce highlight only within selected set when any selection exists
    }
    this.clickedRow = row.rowId;
  }

  public getUserInfo(){
    this.spinnerService.show(true);
    this.masterDataService
      .GetUserInfo("eur")
      .pipe(
        map((res: IUserInfoResponse[]) => {
          this.tableItems = this.tableItems.map(item => ({ ...item, userName:"", email:""}));
          this.userInfos = res;
          for(let i=0;i<this.tableItems.length;i++){
            let userInfo = this.userInfos.find(x => x.PUID === this.tableItems[i][this.puidColumnName]);
            this.tableItems[i].userName = userInfo?.Name;
            this.tableItems[i].email = userInfo?.Email;
          }
        }),
        catchError((): any[] => {
          return [] as any[];
        }),
        finalize(()=>{
          this.userInformationFetched = true;
          this.updateDataSource();
          this.spinnerService.show(false);
        })
      )
      .subscribe();
  }
  public buildCheckboxId(scope: 'master' | 'row', rowId?: number): string {
    const element = scope === 'master' ? 'tbl-chk-master' : 'tbl-chk-row';
    return this.composeId(element, rowId);
  }

  public buildCellWrapperId(column: ColumnConfig, rowId: number, variant: string): string {
    return this.composeId(
      `tbl-cell-${this.slugify(column?.columnName || 'column')}-${this.slugify(variant)}`,
      rowId,
    );
  }

  public buildCellActionId(column: ColumnConfig, rowId: number, action: string): string {
    return this.composeId(
      `tbl-cell-action-${this.slugify(column?.columnName || 'column')}-${this.slugify(action)}`,
      rowId,
    );
  }

  public buildInlineButtonId(column: ColumnConfig, rowId: number, action: string): string {
    return this.composeId(
      `tbl-btn-${this.slugify(column?.columnName || 'column')}-${this.slugify(action)}`,
      rowId,
    );
  }

  private composeId(elementToken: string, rowId?: number | string): string {
    const base = this.resolveBasePrefix();
    const tokens = [base, this.slugify(elementToken)];
    const rowToken = this.formatRowToken(rowId);
    if (rowToken) {
      tokens.push(rowToken);
    }
    return tokens.join('-').replace(/-{2,}/g, '-');
  }

  private resolveBasePrefix(): string {
    if (this.idPrefix && this.idPrefix.trim().length > 0) {
      return this.slugify(this.idPrefix);
    }
    return `${this.defaultFeatureToken}-${this.defaultSurfaceToken}`;
  }

  private formatRowToken(rowId?: number | string): string | null {
    if (rowId === undefined || rowId === null) {
      return null;
    }
    if (typeof rowId === 'number' && rowId >= 0) {
      return `row-${rowId.toString().padStart(2, '0')}`;
    }
    const asString = String(rowId).trim();
    if (!asString) {
      return null;
    }
    if (/^\d+$/.test(asString)) {
      return `row-${asString.padStart(2, '0')}`;
    }
    return `row-${this.slugify(asString)}`;
  }

  private slugify(value: string): string {
    return (value || 'na')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
  }

   public normalizeDateValue(value: unknown): unknown {
    if (!value) {
      return value;
    }

    if (value instanceof Date) {
      return value;
    }

    if (typeof value !== 'string') {
      return value;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return value;
    }

    if (/[zZ]$/.test(trimmed)) {
      return trimmed;
    }

    if (/[+-]\d{2}:\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    return `${trimmed}Z`;
  }
}
